import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, ChevronRight, Upload, Search, Settings, CreditCard, Download, CheckCircle } from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';

const HowItWorksPage = () => {
  useCanonical('https://ecuflashservice.com/how-it-works');
  
  const steps = [
    {
      num: 1,
      icon: Upload,
      title: 'Upload Your File',
      description: 'Read your ECU file using a compatible tool (KESS, KTAG, CMD, etc.) and upload it to our secure platform.',
      details: [
        'Supported formats: .bin, .ori, .mod, .fls, .hex, .mpc',
        'Maximum file size: 50MB',
        'Files are encrypted and stored securely',
        'Your original file is always preserved'
      ]
    },
    {
      num: 2,
      icon: Search,
      title: 'Automatic Analysis',
      description: 'Our system automatically analyzes your file to identify the ECU type, existing calibration, and available modifications.',
      details: [
        'ECU brand and type detection',
        'Current tune identification',
        'DTC code scanning',
        'Checksum type detection'
      ]
    },
    {
      num: 3,
      icon: Settings,
      title: 'Select Services',
      description: 'Choose the modifications you need from our comprehensive list of tuning options.',
      details: [
        'DPF/FAP delete',
        'EGR removal',
        'AdBlue/SCR delete',
        'DTC code removal',
        'Stage 1/2 performance tuning',
        'Speed limiter removal'
      ]
    },
    {
      num: 4,
      icon: CreditCard,
      title: 'Secure Payment',
      description: 'Pay securely via PayPal. We accept all major credit cards and PayPal balance.',
      details: [
        'Secure PayPal checkout',
        'No hidden fees',
        'Instant payment confirmation',
        'Money-back guarantee'
      ]
    },
    {
      num: 5,
      icon: Settings,
      title: 'Professional Modification',
      description: 'Our experienced engineers modify your file according to your specifications.',
      details: [
        'Average turnaround: 20-60 minutes',
        'All modifications verified',
        'Checksum correction included',
        'Quality tested before delivery'
      ]
    },
    {
      num: 6,
      icon: Download,
      title: 'Download & Flash',
      description: 'Download your modified file from the customer portal and flash it to your vehicle.',
      details: [
        'Instant download available',
        'Email notification sent',
        'Free rework if needed',
        '30-day DTC support included'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>How ECU File Service Works | Upload, Modify, Download | 6 Easy Steps</title>
        <meta name="description" content="How our ECU tuning service works. 1) Upload ECU file 2) Auto-analysis 3) Select services 4) Pay securely 5) We modify 6) Download. Average 20-60 min turnaround." />
        <meta name="keywords" content="ECU file service, how ECU tuning works, ECU file upload, remote ECU tuning, ECU modification process" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">Pricing</Link>
              <Link to="/services" className="text-[#888] hover:text-[#00F0FF] text-sm hidden md:block uppercase tracking-wider font-semibold transition">Services</Link>
              <Link to="/" className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-4 py-2 text-sm hover:bg-white transition">
                <span>Get Started</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-white/10 mb-6">
            <Settings className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#888]">Simple Process</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">How Our ECU</span>{' '}
            <span className="text-[#00F0FF]">Service Works</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto">
            Get your ECU professionally modified in 6 simple steps. Fast, secure, and guaranteed.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="space-y-8">
            {steps.map((step, idx) => (
              <div key={step.num} className="relative">
                {/* Connector Line */}
                {idx < steps.length - 1 && (
                  <div className="absolute left-8 top-24 w-0.5 h-full bg-gradient-to-b from-[#00F0FF]/50 to-transparent hidden md:block" />
                )}
                
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-[#00F0FF] flex items-center justify-center">
                      <span className="font-heading text-2xl font-bold text-black">{step.num}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#00F0FF]/30 transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <step.icon className="w-6 h-6 text-[#00F0FF]" />
                      <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white">{step.title}</h3>
                    </div>
                    <p className="text-[#888] mb-4">{step.description}</p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-[#666]">
                          <CheckCircle className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Turnaround Time */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold uppercase tracking-tight text-white mb-8">Average Turnaround Time</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#1A1A1A] border border-white/5 p-6">
              <div className="text-4xl font-bold text-[#00F0FF] mb-2">20-60</div>
              <div className="text-white font-semibold mb-1">Minutes</div>
              <div className="text-[#666] text-sm">Standard modifications</div>
            </div>
            <div className="bg-[#1A1A1A] border border-white/5 p-6">
              <div className="text-4xl font-bold text-[#00F0FF] mb-2">24/7</div>
              <div className="text-white font-semibold mb-1">Service</div>
              <div className="text-[#666] text-sm">We work around the clock</div>
            </div>
            <div className="bg-[#1A1A1A] border border-white/5 p-6">
              <div className="text-4xl font-bold text-[#00F0FF] mb-2">100%</div>
              <div className="text-white font-semibold mb-1">Satisfaction</div>
              <div className="text-[#666] text-sm">Money-back guarantee</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4">Ready to Get Started?</h2>
          <p className="text-[#888] mb-8">Upload your ECU file now and get your modification within minutes</p>
          <Link 
            to="/" 
            className="inline-block btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-white hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
          >
            <span>Start Now →</span>
          </Link>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default HowItWorksPage;
